<script setup>
  import {computed, inject, onMounted, ref, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentCard from "@components/Cards/Card.vue";
  import ComponentContactCard from "@components/Cards/ContactCard.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import ComponentTextarea from "@components/Textarea/Textarea.vue";
  import {router, useForm} from "@inertiajs/vue3";
  import {doctorProfession} from "@pages/Contact/Enums/Enums.js";
  import {MedButton} from "@pharma4u/medicheck-components";
  import {debounce} from "es-toolkit";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({
    name: "ContactsContactForm",
  });

  const props = defineProps({
    contact: {
      type: Object,
      default: null,
    },
    embeddedForm: {
      type: Boolean,
      default: false,
    },
    patientId: {
      type: String,
      default: null,
    },
    redirectBack: {
      type: Boolean,
      default: false,
    },
    emitCancel: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits([
    "submitButtonClicked",
    "cancelButtonClicked",
    "submitHasNoErrors",
    "selectExistingContact",
  ]);

  const broadcast = inject("$broadcast");

  const addressBook = ref([]);
  const existingContacts = ref([]);

  const form = useForm({
    title: props?.contact?.title,
    lastname: props?.contact?.lastname,
    firstname: props?.contact?.firstname,
    profession: props?.contact?.profession,
    email: props?.contact?.email,
    telephone: props?.contact?.telephone,
    fax: props?.contact?.fax,
    company: props?.contact?.company,
    street: props?.contact?.street,
    zip: props?.contact?.zip,
    city: props?.contact?.city,
    note: props?.contact?.note,
    patient_id: props.patientId,
    redirect_back: props.redirectBack,
  });

  onMounted(() => {
    useAuthHttp().post(route("api.contacts.search"), {
      onSuccess: (response) => {
        addressBook.value = response?.data ?? [];
      },
    });

    setTimeout(() => {
      existingContacts.value = searchExistingContacts();
    }, 1000);
  });

  watch(
    () => form.processing,
    (formIsProcessing, formWasProcessing) => {
      if (!formIsProcessing && formWasProcessing && !form.hasErrors) {
        emit("submitHasNoErrors");
      }
    },
  );

  watch(
    [() => form.lastname, () => form.firstname, () => form.company],

    // debounce of callback method
    debounce(() => {
      existingContacts.value = searchExistingContacts();
    }, 1000),
  );

  function searchExistingContacts() {
    const fieldsToSearch = ["firstname", "lastname", "company"];
    // collect non-empty search terms from the form
    const activeSearchTerms = fieldsToSearch.filter((field) => form[field]);
    // early exit when there are no search terms
    if (activeSearchTerms.length === 0) {
      return [];
    }

    return addressBook.value.filter((contact) => {
      // early exit: exclude the contact being updated
      // from the list of existing contacts
      if (isUpdate.value && contact.id === props.contact.id) {
        return false;
      }

      // .every() needs all checks to be true
      return fieldsToSearch.every((field) => {
        const searchTerm = form[field]?.trim().toLowerCase();
        const contactValue = contact[field]?.toLowerCase() || "";

        // search term does not exist => true
        // otherwise -> check if contact field contains search term
        return !searchTerm || contactValue.includes(searchTerm);
      });
    });
  }

  const isUpdate = computed(() => {
    return props.contact !== null && props.contact.id !== undefined && props.contact.id !== null;
  });

  function save() {
    if (isUpdate.value) {
      form.put(route("contacts.update", {contact: props.contact.id}), {});
    } else {
      form.post(route("contacts.store"), {
        preserveScroll: true,
        onSuccess: () => {
          if (form.patient_id !== null) {
            broadcast.patient.postMessage(form.patient_id, {action: "reload.patient"});
          }
        },
      });
    }
  }

  function submitClicked() {
    emit("submitButtonClicked");
  }

  function handleCancel() {
    if (props.emitCancel) {
      emit("cancelButtonClicked");
    } else {
      globalThis.history.back();
    }
  }

  function selectExistingContact(contactId) {
    if (props.embeddedForm) {
      // emit as selected contact
      emit("selectExistingContact", {id: contactId});
    } else {
      // navigate to
      router.get(route("contacts.edit", {contact: contactId}));
    }
  }
</script>

<template>
  <form :data-test="getTestId('contact-form')" @submit.prevent="save">
    <ComponentAlert
      v-if="existingContacts.length > 0"
      type="error"
      :allow-toggle="isUpdate"
      :allow-closing="true"
      :test-id="getTestId('contact-form-duplicates-warning')"
    >
      <template #title>
        <h2 class="text-xl">Eventuell existiert dieser Kontakt bereits?</h2>
      </template>

      <p class="mt-2">
        Sie möchten einen {{ isUpdate ? "bestehenden Kontakt bearbeiten" : "neuen Kontakt erstellen" }}, jedoch
        <template v-if="existingContacts.length === 1">existiert bereits ein Kontakt</template>
        <template v-if="existingContacts.length > 1">existieren bereits mehrere Kontakte</template>
        mit gleichen Angaben.
        <template v-if="isUpdate">Vermeiden Sie, Kontakte doppelt anzulegen.</template>

        <template v-else>
          <template v-if="embeddedForm">
            Wählen Sie ggf. den bestehenden Kontakt aus und vermeiden Sie, einen Kontakt doppelt anzulegen.
          </template>
          <template v-else>
            Wechseln Sie ggf. zum bestehenden Kontakt und vermeiden Sie, einen Kontakt doppelt anzulegen.
          </template>
        </template>
      </p>

      <ul class="mt-4 grid grid-cols-2 gap-4">
        <template v-for="existingContact in existingContacts" :key="existingContact.id">
          <li class="relative">
            <ComponentContactCard class="h-full bg-white text-gray-900" :contact="existingContact" small />

            <div class="absolute right-2 bottom-2 flex space-x-4">
              <MedButton
                :aria-label="embeddedForm ? 'Diesen bestehenden Kontakt auswählen' : 'Zu diesem Kontakt wechseln'"
                variant="primary"
                :icon="embeddedForm ? 'add' : 'open_in_new'"
                :data-test="getTestId('duplicate-contact-card-button')"
                rounded="full"
                @click="selectExistingContact(existingContact.id)"
              />
            </div>
          </li>
        </template>
      </ul>
    </ComponentAlert>

    <ComponentCard class="rounded-lg border-2 border-gray-200" :class="$attrs['class']" :test-id="getTestId('form')">
      <div class="grid grid-cols-4 gap-8">
        <ComponentInput
          v-model="form.company"
          class="col-span-2"
          label="Praxis / Institution / Firma**"
          :hide-clear="true"
          :validation="form.errors.company"
          :test-id="getTestId('contact-form-company')"
        />

        <ComponentSelect
          v-model="form.profession"
          class="col-span-2"
          label="Fachrichtung"
          :validation="form.errors.profession"
          :options="doctorProfession"
          :nullable="true"
          placeholder-text=""
          :test-id="getTestId('contact-form-profession')"
        />

        <ComponentInput
          v-model="form.title"
          class="col-start-1"
          label="Titel"
          :hide-clear="true"
          :validation="form.errors.title"
          :test-id="getTestId('contact-form-title')"
        />

        <ComponentInput
          v-model="form.firstname"
          class="col-span-2 col-start-1"
          label="Vorname"
          :hide-clear="true"
          :validation="form.errors.firstname"
          :test-id="getTestId('contact-form-firstname')"
        />

        <ComponentInput
          v-model="form.lastname"
          class="col-span-2"
          label="Nachname**"
          :hide-clear="true"
          :validation="form.errors.lastname"
          :test-id="getTestId('contact-form-lastname')"
        />

        <ComponentInput
          v-model="form.street"
          class="col-span-4 col-start-1"
          label="Straße, Hausnr."
          :hide-clear="true"
          :validation="form.errors.street"
          :test-id="getTestId('contact-form-street')"
        />

        <ComponentInput
          v-model="form.zip"
          class="col-span-1 col-start-1"
          label="PLZ"
          :hide-clear="true"
          :validation="form.errors.zip"
          :test-id="getTestId('contact-form-zip')"
        />

        <ComponentInput
          v-model="form.city"
          class="col-span-3"
          label="Ort"
          :hide-clear="true"
          :validation="form.errors.city"
          :test-id="getTestId('contact-form-city')"
        />

        <ComponentInput
          v-model="form.telephone"
          class="col-span-2 col-start-1"
          label="Telefon"
          :hide-clear="true"
          :validation="form.errors.telephone"
          :test-id="getTestId('contact-form-telephone')"
        />

        <ComponentInput
          v-model="form.fax"
          class="col-span-2"
          label="Fax"
          :hide-clear="true"
          :test-id="getTestId('contact-form-fax')"
        />

        <ComponentInput
          v-model="form.email"
          class="col-span-2 col-start-1"
          type="email"
          label="E-Mail"
          :hide-clear="true"
          :validation="form.errors.email"
          :test-id="getTestId('contact-form-email')"
        />

        <ComponentTextarea
          v-if="!embeddedForm"
          v-model="form.note"
          class="col-span-4 col-start-1"
          label="Zusätzliche Informationen"
          :validation="form.errors.note"
          :test-id="getTestId('contact-form-note')"
        />
      </div>

      <div class="text-right text-sm" :data-test="getTestId('contact-form-mandatory-information')">
        <!-- TODO: why ** and not *-->
        ** mind. eine Angabe erforderlich
      </div>

      <template #actions>
        <MedButton
          variant="primary"
          :disabled="form.processing"
          :data-test="getTestId('contact-form-submit-button')"
          type="submit"
          @click="submitClicked"
        >
          Speichern
        </MedButton>

        <MedButton
          :disabled="form.processing"
          :data-test="getTestId('contact-form-cancel-button')"
          type="button"
          @click="handleCancel"
        >
          Abbrechen
        </MedButton>
      </template>
    </ComponentCard>
  </form>
</template>
